import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import componentStyles from './tufts-office-hours.css.lit';
import tuftsHeadingCssLit from '../tufts-heading/tufts-heading.css.lit';
import '../tufts-heading/tufts-heading';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotNGController } from '../../controllers/slot-ng-controller';

/**
 * The tuftsOfficeHours component
 * @element tufts-office-hours
 *
 * @slot status
 * @slot hours
 */
@customElement('tufts-office-hours')
export class tuftsOfficeHours extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, tuftsHeadingCssLit];
  ngSlot = new SlotNGController(this);
  currentDate: Date = new Date();
  isOpenTheHospital: string;

  connectedCallback() {
    super.connectedCallback();
    this.updateHospitalStatus();
  }

  updateHospitalStatus() {
    const userTime = this.getCurrentDayAndTime();
    const hospitalSchedule = this.getTimeHoursOpen();
    this.isOpenTheHospital = this.isHospitalOpen(userTime, hospitalSchedule);
  }

  getCurrentDayAndTime(): { day: string; time: string } {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const day = days[this.currentDate.getDay()];
    // Convert the current date to a time string in the New York timezone
    // in case they are in different time zones.
    // This is done because the hospital's schedule is in the New York timezone,
    // so we need to compare the user's time to the hospital's schedule in the same timezone.
    const time = this.currentDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: 'America/New_York',
    });
    return { day, time };
  }

  getTimeHoursOpen() {
    const schedule: { day: string | null; time: string[] }[] = [];
    this.querySelectorAll('[slot="hours"]').forEach(element => {
      const spans = element.querySelectorAll('span');
      if (spans.length >= 2) {
        schedule.push({
          day: spans[0].textContent,
          time: spans[1].innerHTML.split('<br>').map(time => time.trim()),
        });
      }
    });
    return schedule;
  }

  isHospitalOpen(
    userTime: { day: string; time: string },
    hospitalSchedule: { day: string | null; time: string[] }[]
  ): string {
    const daySchedule = hospitalSchedule.find(schedule =>
      schedule.day?.includes(userTime.day)
    );
    if (!daySchedule || daySchedule.time[0] === 'Closed') {
      return ' Currently closed';
    }
    if (daySchedule.time[0] === 'Open all day') {
      return 'Currently open';
    }
    const userDate = this.getTimeAsDate(userTime.time);
    return daySchedule.time.some(time =>
      this.isTimeWithinInterval(userDate, time)
    )
      ? 'Currently open'
      : 'Currently closed';
  }

  getTimeAsDate(time: string): Date {
    return new Date(`${this.currentDate.toDateString()} ${time}`);
  }

  isTimeWithinInterval(userDate: Date, interval: string): boolean {
    const [startTime, endTime] = interval
      .split('-')
      .map(t => this.getTimeAsDate(t.trim()));
    return userDate >= startTime && userDate <= endTime;
  }

  contentTemplate(): TemplateResult {
    return html`<details class="additional-location-wrapper">
      <summary>
        ${this.ngSlot.conditionalNGSlot('status')} ${this.isOpenTheHospital}
      </summary>
      ${this.ngSlot.conditionalNGSlot('hours')}
      <div class="additional-info">
        Holidays may affect operating hours. All times in Eastern Time (EDT)
      </div>
    </details> `;
  }

  render(): TemplateResult {
    return html` ${this.contentTemplate()} `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-office-hours': tuftsOfficeHours;
  }
}
